import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BRAND from '../config/brand';
import { Card, Button, Tag, Rate, Typography, Tabs, Spin, message, Badge, Avatar, Divider, Space, Tooltip } from 'antd';
import { 
  DownloadOutlined, 
  FileTextOutlined, 
  HistoryOutlined,
  SafetyOutlined,
  UserOutlined,
  StarOutlined,
  HeartOutlined,
  ShareAltOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  BookOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { skillsApi } from '../services/api';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;

const DetailContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const HeaderSection = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const IconSection = styled.div`
  flex-shrink: 0;
`;

const SkillIcon = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: white;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
`;

const InfoSection = styled.div`
  flex: 1;
`;

const SkillTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  h1 {
    font-size: 32px;
    font-weight: 600;
    margin: 0;
  }
`;

const SkillMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  color: #666;
  font-size: 14px;

  .author {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const SkillDesc = styled(Paragraph)`
  font-size: 16px;
  line-height: 1.6;
  color: #333;
  margin-bottom: 20px;
`;

const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 16px 0;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 20px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  .value {
    font-size: 24px;
    font-weight: 600;
    color: #333;
  }

  .label {
    font-size: 12px;
    color: #999;
  }
`;

const ActionBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
`;

const InstallButton = styled(Button)`
  height: 48px;
  font-size: 16px;
  border-radius: 8px;
`;

const SecondaryButton = styled(Button)`
  height: 48px;
  border-radius: 8px;
`;

const ContentCard = styled(Card)`
  border-radius: 12px;
  margin-bottom: 24px;
`;

const InstallMethod = styled.div`
  background: #f6f8fa;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;

  .method-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .method-desc {
    color: #666;
    margin-bottom: 16px;
    line-height: 1.6;
  }

  .code-block {
    background: #1e1e1e;
    border-radius: 8px;
    padding: 16px;
    color: #d4d4d4;
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 14px;
    position: relative;
    overflow: hidden;

    pre {
      margin: 0;
      white-space: pre-wrap;
      word-break: break-all;
    }

    .copy-btn {
      position: absolute;
      top: 8px;
      right: 8px;
      opacity: 0;
      transition: opacity 0.2s;
    }

    &:hover .copy-btn {
      opacity: 1;
    }
  }
`;

const VersionItem = styled.div`
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  .version-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .version-number {
    font-size: 16px;
    font-weight: 600;
  }

  .version-date {
    color: #999;
    font-size: 13px;
  }

  .version-changes {
    color: #666;
    font-size: 14px;
    line-height: 1.6;
  }
`;

const OverviewSection = styled.div`
  .section-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .feature-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      padding: 8px 0;
      display: flex;
      align-items: flex-start;
      gap: 8px;

      &::before {
        content: '✓';
        color: #52c41a;
        font-weight: bold;
      }
    }
  }
`;

function SkillDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchSkillDetail();
  }, [id]);

  const fetchSkillDetail = async () => {
    try {
      setLoading(true);
      const res = await skillsApi.getSkillDetail(id);
      setSkill(res.data.data);
    } catch (error) {
      console.error('获取技能详情失败:', error);
      message.error('获取技能详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      await skillsApi.downloadSkill(id);
      message.success('已开始下载');
    } catch (error) {
      message.error('下载失败');
    }
  };

  const handleInstallClick = () => {
    setActiveTab('install');
    // 滚动到安装方式区域
    setTimeout(() => {
      const installSection = document.querySelector('.ant-tabs-content');
      if (installSection) {
        installSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleCopyInstall = () => {
    const installCommand = `openclaw skills install ${skill?.name}`;
    navigator.clipboard.writeText(installCommand);
    message.success('安装命令已复制到剪贴板');
  };

  const handleCopyPrompt = () => {
    const prompt = `安装技能 ${skill?.displayName} (${skill?.name})`;
    navigator.clipboard.writeText(prompt);
    message.success('提示词已复制到剪贴板');
  };

  const formatNumber = (num) => {
    if (num >= 10000) {
      return (num / 10000).toFixed(1) + '万';
    }
    return num.toString();
  };

  if (loading) {
    return (
      <DetailContainer>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size="large" />
        </div>
      </DetailContainer>
    );
  }

  if (!skill) {
    return (
      <DetailContainer>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Title level={4}>技能不存在</Title>
          <Button type="primary" onClick={() => navigate('/skills')} style={{ marginTop: 16 }}>
            返回技能列表
          </Button>
        </div>
      </DetailContainer>
    );
  }

  return (
    <DetailContainer>
      <HeaderSection>
        <IconSection>
          <SkillIcon>{skill.displayName?.charAt(0) || 'S'}</SkillIcon>
        </IconSection>
        
        <InfoSection>
          <SkillTitle>
            <h1>{skill.displayName}</h1>
            {skill.type === 1 && <Tag color="blue">官方</Tag>}
            <Tag color="green">通过安全检测</Tag>
          </SkillTitle>

          <SkillMeta>
            <span className="author">
              <Avatar size="small" icon={<UserOutlined />} />
              <Text strong>{skill.authorName}</Text>
            </span>
            <span>源自 ClawHub</span>
            <span>v{skill.version}</span>
          </SkillMeta>

          <SkillDesc>{skill.description}</SkillDesc>

          <StatsBar>
            <StatItem>
              <div className="value">{skill.rating}</div>
              <div className="label">AI 评分</div>
            </StatItem>
            <StatItem>
              <div className="value">{formatNumber(skill.downloadCount)}</div>
              <div className="label">下载量</div>
            </StatItem>
            <StatItem>
              <div className="value">{formatNumber(skill.ratingCount || 0)}</div>
              <div className="label">收藏</div>
            </StatItem>
          </StatsBar>

          <ActionBar>
            <InstallButton 
              type="primary" 
              icon={<DownloadOutlined />} 
              onClick={handleInstallClick}
            >
              安装技能
            </InstallButton>
            <SecondaryButton icon={<HeartOutlined />}>
              收藏
            </SecondaryButton>
            <SecondaryButton icon={<ShareAltOutlined />}>
              分享
            </SecondaryButton>
          </ActionBar>
        </InfoSection>
      </HeaderSection>

      <ContentCard>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          size="large"
        >
          <TabPane 
            tab={<><BookOutlined /> 概述</>} 
            key="overview"
          >
            <OverviewSection>
              <div className="section-title">
                <SafetyOutlined /> 功能介绍
              </div>
              <Paragraph style={{ fontSize: 15, lineHeight: 1.8 }}>
                {skill.description}
              </Paragraph>
              
              <Divider />
              
              <div className="section-title">
                <CheckCircleOutlined /> 核心特性
              </div>
              <ul className="feature-list">
                <li>专为音乐行业设计，深度适配行业需求</li>
                <li>AI 驱动，智能高效</li>
                <li>安全可靠，通过多重安全检测</li>
                <li>持续更新，功能不断完善</li>
              </ul>

              <Divider />

              <div className="section-title">
                <FileTextOutlined /> 使用说明
              </div>
              <Paragraph>
                安装后，您可以通过以下方式使用该技能：
              </Paragraph>
              <div 
                dangerouslySetInnerHTML={{ 
                  __html: skill.usageExample?.replace(/```[\w]*\n?/g, '') || '' 
                }}
                style={{ 
                  background: '#f6f8fa', 
                  padding: 16, 
                  borderRadius: 8,
                  fontFamily: 'monospace',
                  fontSize: 14
                }}
              />
            </OverviewSection>
          </TabPane>

          <TabPane 
            tab={<><CodeOutlined /> 安装方式</>} 
            key="install"
          >
            {/* 顶部说明 */}
            <div style={{ marginBottom: 24, padding: '16px', background: '#f6f8fa', borderRadius: '8px', borderLeft: '4px solid #1890ff' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px', color: '#1890ff' }}>
                💡 复制提示词，发送给任意 AI 助手即可安装 Skill
              </div>
              <div style={{ color: '#666', fontSize: '14px' }}>
                支持 Lighthouse OpenClaw、WorkBuddy、QClaw、Kimi、Claude、Cursor 等主流 AI Agent
              </div>
            </div>

            <Tabs defaultActiveKey="chat" type="card" size="large" style={{ marginTop: 8 }}>
              <TabPane tab={<><FileTextOutlined /> 💬 对话安装</>} key="chat">

            {/* 方式一：对话安装 - 安装商店和技能 */}
            <InstallMethod style={{ border: '2px solid #1890ff', background: '#f0f7ff' }}>
              <div className="method-title">
                <FileTextOutlined /> 方式一：安装{BRAND.name}和技能
              </div>
              <div className="method-desc">
                复制下方提示词发送给 AI Agent，Agent 会自动检测并安装百音技能商店，然后安装本技能。
              </div>
              <div className="code-block" style={{ minHeight: '180px' }}>
                <pre>{`请先检查是否已安装百音技能商店，若未安装，请根据 https://skillshub.hikoon.com/install/baiyin.md 安装百音技能商店，但是只安装CLI，然后安装 ${skill.name} 技能。

若已安装百音技能商店，则直接安装 ${skill.name} 技能。

安装完成后，请回复"技能安装完成"，并告诉我你可以使用这个技能帮我做什么。`}</pre>
                <Button 
                  className="copy-btn" 
                  size="small" 
                  icon={<CopyOutlined />}
                  onClick={() => {
                    const prompt = `请先检查是否已安装百音技能商店，若未安装，请根据 https://skillshub.hikoon.com/install/baiyin.md 安装百音技能商店，但是只安装CLI，然后安装 ${skill.name} 技能。

若已安装百音技能商店，则直接安装 ${skill.name} 技能。

安装完成后，请回复"技能安装完成"，并告诉我你可以使用这个技能帮我做什么。`;
                    navigator.clipboard.writeText(prompt);
                    message.success('提示词已复制到剪贴板');
                  }}
                >
                  复制
                </Button>
              </div>
            </InstallMethod>

            {/* 方式二：对话安装 - 设为优先源 */}
            <InstallMethod>
              <div className="method-title">
                <FileTextOutlined /> 方式二：设为优先技能安装源
              </div>
              <div className="method-desc">
                将百音技能商店设为优先技能安装源，加速后续技能安装：
              </div>
              <div className="code-block" style={{ minHeight: '180px' }}>
                <pre>{`请先检查是否已安装百音技能商店，若未安装，请根据 https://skillshub.hikoon.com/install/baiyin.md 安装百音技能商店，然后安装 ${skill.name} 技能。

若已安装百音技能商店，则直接安装 ${skill.name} 技能，并将百音技能商店设为优先技能安装源。

安装完成后，请回复"技能安装完成"，并告诉我你可以使用这个技能帮我做什么。`}</pre>
                <Button 
                  className="copy-btn" 
                  size="small" 
                  icon={<CopyOutlined />}
                  onClick={() => {
                    const prompt = `请先检查是否已安装百音技能商店，若未安装，请根据 https://skillshub.hikoon.com/install/baiyin.md 安装百音技能商店，然后安装 ${skill.name} 技能。

若已安装百音技能商店，则直接安装 ${skill.name} 技能，并将百音技能商店设为优先技能安装源。

安装完成后，请回复"技能安装完成"，并告诉我你可以使用这个技能帮我做什么。`;
                    navigator.clipboard.writeText(prompt);
                    message.success('提示词已复制到剪贴板');
                  }}
                >
                  复制
                </Button>
              </div>
            </InstallMethod>

              </TabPane>

              <TabPane tab={<><CodeOutlined /> 💻 命令行安装</>} key="cli">

            {/* 方式三：命令行安装 */}
            <InstallMethod>
              <div className="method-title">
                <CodeOutlined /> 方式三：命令行安装
              </div>
              <div className="method-desc">
                在终端中执行以下命令，即可安装百音技能商店 CLI：
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#333' }}>步骤 01：安装百音技能商店</div>
                <div className="code-block">
                  <pre>curl -fsSL https://skillshub.hikoon.com/install/install.sh | bash</pre>
                  <Button 
                    className="copy-btn" 
                    size="small" 
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText('curl -fsSL https://skillshub.hikoon.com/install/install.sh | bash');
                      message.success('命令已复制到剪贴板');
                    }}
                  >
                    复制
                  </Button>
                </div>
                <div style={{ marginTop: 8, color: '#666', fontSize: '13px' }}>
                  // 复制命令到终端执行，安装 CLI 并优先采用百音技能商店加速安装技能
                </div>
              </div>

              <div>
                <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#333' }}>步骤 02：安装完 CLI 后，安装技能</div>
                <div className="code-block">
                  <pre>baiyin install {skill.name}</pre>
                  <Button 
                    className="copy-btn" 
                    size="small" 
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(`baiyin install ${skill.name}`);
                      message.success('命令已复制到剪贴板');
                    }}
                  >
                    复制
                  </Button>
                </div>
              </div>
            </InstallMethod>

              </TabPane>

              <TabPane tab={<><DownloadOutlined /> 📦 ZIP包安装</>} key="zip">

            {/* 方式四：ZIP包安装 */}
            <InstallMethod>
              <div className="method-title">
                <DownloadOutlined /> 方式四：ZIP包安装
              </div>
              
              <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
                <div style={{ flex: 1, textAlign: 'center', padding: '20px', background: '#f6f8fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>1</div>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>下载</div>
                  <div style={{ color: '#666', fontSize: '13px' }}>从我们的仓库获取最新的源码包</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', padding: '20px', background: '#f6f8fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>2</div>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>解压</div>
                  <div style={{ color: '#666', fontSize: '13px' }}>将技能目录解压到您首选的文件夹</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', padding: '20px', background: '#f6f8fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>3</div>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>运行</div>
                  <div style={{ color: '#666', fontSize: '13px' }}>把 skill 目录放到对应 agent 的 skill 目录下</div>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
                  下载 {skill.name}.zip 安装包
                </Button>
              </div>

              {/* 目录树结构 */}
              <div style={{ background: '#1e1e1e', borderRadius: '8px', padding: '16px', color: '#d4d4d4', fontFamily: 'monospace', fontSize: '13px' }}>
                <div style={{ marginBottom: '8px', color: '#6cc644' }}>📁 {skill.name}/</div>
                <div style={{ paddingLeft: '20px' }}>
                  <div>📄 SKILL.md <span style={{ color: '#858585' }}>← 技能定义文件</span></div>
                  <div>📄 README.md <span style={{ color: '#858585' }}>← 使用说明</span></div>
                  <div>📁 scripts/ <span style={{ color: '#858585' }}>← 脚本文件</span></div>
                </div>
              </div>
            </InstallMethod>

              </TabPane>
            </Tabs>
          </TabPane>

          <TabPane 
            tab={<><HistoryOutlined /> 版本历史</>} 
            key="history"
          >
            <VersionItem>
              <div className="version-header">
                <span className="version-number">v{skill.version}</span>
                <span className="version-date">{new Date(skill.updatedAt).toLocaleDateString('zh-CN')}</span>
              </div>
              <div className="version-changes">
                <Tag color="green">当前版本</Tag>
                <p>最新稳定版本，包含所有最新功能和修复。</p>
              </div>
            </VersionItem>
            
            {skill.version !== '1.0.0' && (
              <VersionItem>
                <div className="version-header">
                  <span className="version-number">v1.0.0</span>
                  <span className="version-date">{new Date(skill.createdAt).toLocaleDateString('zh-CN')}</span>
                </div>
                <div className="version-changes">
                  <p>初始版本发布</p>
                </div>
              </VersionItem>
            )}
          </TabPane>
        </Tabs>
      </ContentCard>
    </DetailContainer>
  );
}

export default SkillDetail;
